import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "../../ui/button";
import {
    Monitor,
    ShieldCheck,
    Cpu,
    Loader2,
    Clock,
    Check
} from "lucide-react";
import { Device, DeviceLoadingState } from "../interfaces/EndpointInterfaces";
import StatusIndicator from "../Endpoints/StatusIndicator";
import RequestConnectAccessDialog from "./RequestConnectAccessDialog";
import CardPagination from "../CardPagination";
import "./TenantEndpoints.css";
import { getAllWindowsCredentials, getUsers } from "./Api/tenantuserendpointsapi";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import { FaLinux, FaWindows } from "react-icons/fa";
import EmptyStateCard from "../EmptyStateCard";
import { FaApple } from "react-icons/fa6";
import useNotificationStore from "../../../services/notificationStore";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { NGROK } from "../../../APIs";
import {
    LIST_STATE_KEYS,
    writePersistedListState,
} from "../../../utils/listPaginationState";

interface TenantDeviceTableProps {
    devices: Device[];
    onConnect?: (device: Device, mode: string, label: string) => void;
    isLoading: boolean | false;
    totalDevicesCount: number;
    deviceLoadingState: Record<string, DeviceLoadingState>;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;

}

const DEVICES_PER_PAGE = 15;

export default function TenantDeviceTable({
    devices,
    onConnect,
    isLoading,
    totalDevicesCount,
    deviceLoadingState,
    page,
    setPage
}: TenantDeviceTableProps) {
    const navigate = useNavigate();
    const [showAccessDialog, setShowAccessDialog] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [usersList, setUsersList] = useState<any[]>([]);
    const [pendingRequestDeviceIds, setPendingRequestDeviceIds] = useState<Record<string, boolean>>({});
    let tenant = myLocalStorage.getItem("latestTenant");
    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;
    let { email, role, userRole } = useUserStore((state) => state.user);
    const { userApprovedNotifications, setUserApprovedNotifications } =
        useNotificationStore();
    const isFetchingSummaryRef = useRef(false);
    const [remainingTimes, setRemainingTimes] = useState<Record<string, string>>({});
    const [, setRemainingSeconds] = useState(30 * 60);

    const isOwnDevices = (device: Device): boolean => {
        return device.belongedToUser === email;
    }

    const getApprovedAccessNotification = (deviceId: string) => {
        return userApprovedNotifications.find(
            (item: any) =>
                Number(item.computerId) === Number(deviceId) &&
                item.requestStatus === "APPROVED" &&
                item.resourceType === "GROUP" &&
                item.resourceName === "Whiteswan Access"
        );
    };


    const shouldShowConnectButton = (device: Device): boolean => {
        const item = getApprovedAccessNotification(device.id);
        if (!item) {
            return false;
        }

        const nowUTC = moment.utc();

        // Scheduled access: timer/connect driven by schedule window only.
        if (item.schedules && item.schedules.length > 0) {
            return item.schedules.some((schedule: any) =>
                nowUTC.isBetween(
                    moment.utc(schedule.scheduledStartTime),
                    moment.utc(schedule.scheduledEndTime),
                    null,
                    "[]",
                ),
            );
        }

        if (item.accessTimeOverAt && nowUTC.isAfter(moment.utc(item.accessTimeOverAt))) {
            return false;
        }

        return true;
    };

    const getAccessEndMoment = (item: any): moment.Moment | null => {
        if (!item) return null;

        if (item.schedules && item.schedules.length > 0) {
            const nowUTC = moment.utc();
            const activeSchedule = item.schedules.find((schedule: any) =>
                nowUTC.isBetween(
                    moment.utc(schedule.scheduledStartTime),
                    moment.utc(schedule.scheduledEndTime),
                    null,
                    "[]",
                ),
            );
            if (!activeSchedule) return null;
            return moment.utc(activeSchedule.scheduledEndTime);
        }

        if (item.accessTimeOverAt) {
            const raw = item.accessTimeOverAt;
            return moment.utc(raw.endsWith("Z") ? raw : `${raw}Z`);
        }

        return null;
    };

    const refreshApprovedAccessNotifications = useCallback(async () => {
        if (!email || !tenantName || isFetchingSummaryRef.current) return;
        isFetchingSummaryRef.current = true;

        const controller = new AbortController();

        try {
            const params: Record<string, string> = {
                role,
                tenantName,
            };

            if (!email.startsWith("wsadmin")) {
                params.email = email;
            }

            const response = await axios.get(`${NGROK}/api/access/summary`, {
                params,
                signal: controller.signal,
            });

            if (response?.data?.onPremAccess) {
                setUserApprovedNotifications(response.data.onPremAccess);
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Error fetching summary:", error);
            }
        } finally {
            isFetchingSummaryRef.current = false;
        }
    }, [email, role, tenantName, setUserApprovedNotifications]);

    const handleRequestSubmitSuccess = useCallback(async () => {
        if (!selectedDevice?.id) {
            await refreshApprovedAccessNotifications();
            return;
        }

        setPendingRequestDeviceIds((prev) => ({
            ...prev,
            [selectedDevice.id]: true,
        }));

        await refreshApprovedAccessNotifications();
    }, [refreshApprovedAccessNotifications, selectedDevice]);

    const isDeviceSSh = (device: Device) => {
        return (
            !device?.isManuallyAddedMachine &&
            (
                device?.OperatingSystem?.toLowerCase().includes("linux") ||
                device?.OperatingSystem?.toLowerCase().includes("macos")
            )
        );
    };

    const labelStatus = (mode: string, osmode: string) => {
        let status = mode
        if (mode === "INHERITED_FROM_PROFILE") status = osmode;
        if (mode === "INHERITED_FROM_PROFILE" && osmode === "INHERITED_FROM_TENANT") status = tenant.mode
        return status.toUpperCase();

    }

    const fetchAllUsers = async (device: Device) => {
        if (device?.isManuallyAddedMachine) {
            const response: any = await getAllWindowsCredentials(tenantName, device.id);
            setUsersList(response || []);
            return;
        }

        if (isDeviceSSh(device)) {

            let response = await getUsers(tenantName, device.id, email);
            if (response) {
                let content: any[] = response;

                if (userRole === "epam-user" ||
                    userRole === "server-pam-user" ||
                    userRole === "developer-user" ||
                    userRole === "thirdparty-user") {
                    content = response.filter((data: any) => data.cn !== "wsadmin");
                }
                setUsersList(content);
            }

        } else {
            let response = await getAllWindowsCredentials(tenantName, device.id);
            setUsersList(response);
        };
    }

    const isDeviceWindows = (device: Device): boolean => {
        if (device.OperatingSystem && device.OperatingSystem.trim().length > 0) {
            return device.OperatingSystem.toLowerCase().includes("windows");
        }
        return false;
    };

    const handleConnect = (deviceId: string) => {
        const device = devices.find((d) => d.id === deviceId);
        setUsersList([])
        if (!device) return;
        const deviceNotification = getApprovedAccessNotification(device.id);
        const canConnectImmediately = device?.hasAccess || shouldShowConnectButton(device);
        console.log("Device Notification:", deviceNotification);

        const mode = deviceNotification?.connectionMode || "";
        const label = deviceNotification?.connectionLabel || "";
        if (canConnectImmediately) {

            if (isDeviceWindows(device) || device?.isManuallyAddedMachine) {
                onConnect?.(device, mode, label);
            } else {
                onConnect?.(device, "", "");
            }

        } else if (device) {
            fetchAllUsers(device);
            setSelectedDevice(device);
            setShowAccessDialog(true);
        }

    };


    const getOSIcon = (osString: string) => {
        const os = osString.toLowerCase();
        const sizeClass = "w-7 h-7";

        if (os.includes("windows"))
            return <FaWindows className={`${sizeClass} text-blue-600 dark:text-blue-400`} />;
        if (os.includes("mac") || os.includes("darwin"))
            return <FaApple className={`${sizeClass} text-gray-800 dark:text-white`} />; // white in dark mode
        if (
            os.includes("ubuntu") ||
            os.includes("centos") ||
            os.includes("rhel") ||
            os.includes("rocky") ||
            os.includes("linux")
        )
            return <FaLinux className={`${sizeClass} text-black dark:text-white`} />; // white in dark mode

        return <FaLinux className={`${sizeClass} text-gray-500 dark:text-gray-400`} />;
    };


    const getOSType = (device: Device): "windows" | "linux" | "manual" => {
        if (device?.isManuallyAddedMachine) return "manual";
        const os = device.os.toLowerCase();
        if (os.includes("windows")) return "windows";
        if (os.includes("linux") || os.includes("ubuntu") || os.includes("mac") || os.includes("darwin"))
            return "linux";
        return "linux";
    };

    const handleDeviceClick = (deviceId: string) => {
        const device = devices.find((d) => d.id === deviceId);
        if (device) {
            if (device?.hasAccess || shouldShowConnectButton(device) || isOwnDevices(device)) {
                writePersistedListState(LIST_STATE_KEYS.endpoints, { page });
                navigate(`/endpoints/tenantdevice/${deviceId}`, { state: { device } });
            }
        }

    };

    function getRemainingTime(device: Device) {
        const deviceNotification = getApprovedAccessNotification(device.id);
        if (!deviceNotification || !shouldShowConnectButton(device)) {
            return null;
        }

        const endMoment = getAccessEndMoment(deviceNotification);
        if (!endMoment || !endMoment.isValid()) {
            return null;
        }

        const timeDiff = endMoment.diff(moment.utc());
        if (timeDiff <= 0) {
            return "Expired";
        }

        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

        return `${hours}h ${minutes}m ${seconds}s`;
    }


    useEffect(() => {
        const updateAllRemainingTimes = () => {
            setRemainingTimes((prev) => {
                const updated: Record<string, string> = {};

                devices.forEach((device) => {
                    const time = getRemainingTime(device);
                    if (time && time !== "Expired") {
                        updated[device.id] = time;
                    }
                });

                return updated;
            });
        };

        // Run immediately and then every second
        updateAllRemainingTimes();
        const interval = setInterval(updateAllRemainingTimes, 1000);

        return () => clearInterval(interval);
    }, [devices, userApprovedNotifications]);

    useEffect(() => {
        if (!email || !tenantName) return;

        refreshApprovedAccessNotifications();
        const interval = setInterval(refreshApprovedAccessNotifications, 5000);

        return () => {
            clearInterval(interval);
        };
    }, [email, tenantName, refreshApprovedAccessNotifications]);

    useEffect(() => {
        if (Object.keys(pendingRequestDeviceIds).length === 0) {
            return;
        }

        const approvedDeviceIds = new Set(
            userApprovedNotifications
                .filter((item: any) => item.requestStatus === "APPROVED" && item.resourceName === "Whiteswan Access")
                .map((item: any) => String(item.computerId))
        );

        setPendingRequestDeviceIds((prev) => {
            const next = { ...prev };
            let changed = false;

            Object.keys(next).forEach((deviceId) => {
                if (approvedDeviceIds.has(deviceId)) {
                    delete next[deviceId];
                    changed = true;
                }
            });

            return changed ? next : prev;
        });
    }, [userApprovedNotifications, pendingRequestDeviceIds]);
    useEffect(() => {
        const timer = setInterval(() => {
            setRemainingSeconds((prev) => {
                if (prev <= 0) return 0;
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const getOperatingModeColor = (mode: string, osMode: string) => {
        let status = labelStatus(mode, osMode);
        switch (status.toLowerCase()) {
            case "enforcing":
                return "destructive";
            case "auditing":
                return "default";
            case "disabled":
                return "secondary";
            default:
                return "secondary";
        }
    };

    const totalPages = Math.ceil(totalDevicesCount / DEVICES_PER_PAGE);

    const handlePageChange = (page: number) => {
        setPage(page);
        setCurrentPage(page);
    };

    const renderSkeletonCards = () => {
        return Array.from({ length: DEVICES_PER_PAGE }).map((_, index) => (
            <div key={`skeleton-${index}`} className="tenant-device-card" aria-hidden="true">
                <div className="tenant-device-head">
                    <span className="tenant-skeleton tenant-skeleton-icon" />
                    <div className="tenant-device-identity">
                        <span className="tenant-skeleton" style={{ width: "70%", height: 16 }} />
                        <span className="tenant-skeleton" style={{ width: "45%", height: 12 }} />
                    </div>
                </div>
                <div className="tenant-device-meta">
                    <span className="tenant-skeleton" style={{ width: 96, height: 12 }} />
                    <span className="tenant-skeleton" style={{ width: 72, height: 20 }} />
                </div>
                <div className="tenant-device-actions">
                    <span className="tenant-skeleton" style={{ width: "100%", height: 36 }} />
                </div>
            </div>
        ));
    };


    return (
        <section className="tenant-endpoints-card">
            <div className="tenant-endpoints-scroll">
                {!isLoading && devices.length === 0 ? (
                    <EmptyStateCard
                        icon={Monitor}
                        title="No devices available"
                        description="No devices are currently assigned to your roles."
                        className="tenant-endpoints-empty"
                        embedded
                    />
                ) : (
                    <div className="tenant-endpoints-grid">
                        {isLoading ? (
                            renderSkeletonCards()
                        ) : (
                            devices.map((device) => (
                                <div
                                    key={device.id}
                                    className="tenant-device-card"
                                    data-interactive="true"
                                    onClick={() => {
                                        if (!device.isManuallyAddedMachine)
                                            handleDeviceClick(device.id)
                                    }}
                                >
                                    <div className="tenant-device-head">
                                        <span className="tenant-device-os">
                                            {getOSIcon(device.os)}
                                        </span>
                                        <div className="tenant-device-identity">
                                            <h4
                                                className="tenant-device-name"
                                                data-testid={`text-device-name-${device.id}`}
                                            >
                                                {device.friendlyName ? device.friendlyName : device.name}
                                            </h4>
                                            {device.friendlyName && (
                                                <span className="tenant-device-host">
                                                    Host Name: {device.name}
                                                </span>
                                            )}
                                            <div className="tenant-device-status">
                                                <StatusIndicator lastSeen={device.lastSeen} showDot isManualDevice={device?.isManuallyAddedMachine || false} />
                                                <span className="tenant-device-os-name">{device.os}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="tenant-device-meta">
                                        <span className="tenant-device-agent">
                                            <Cpu aria-hidden="true" />
                                            Agent
                                            <strong>v{device.agentVersion}</strong>
                                        </span>
                                        <span
                                            className="tenant-device-mode"
                                            data-tone={getOperatingModeColor(device.mode || "", device.profileMode || "")}
                                        >
                                            {labelStatus(device.mode || "", device.profileMode || "")}
                                        </span>
                                    </div>

                                    <div
                                        className="tenant-device-actions"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {isOwnDevices(device) ?
                                            <span className="tenant-device-assigned">
                                                <Check aria-hidden="true" />
                                                Assigned
                                            </span> :
                                            shouldShowConnectButton(device) ?
                                                <div className="tenant-device-connect">
                                                    {remainingTimes[device.id] && (
                                                        <span className="tenant-device-timer">
                                                            <Clock aria-hidden="true" />
                                                            {remainingTimes[device.id]}
                                                        </span>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        disabled={deviceLoadingState[device.id]?.loading}
                                                        onClick={() => handleConnect(device.id)}
                                                        data-testid={`button-connect-${device.id}`}
                                                        className="tenant-device-primary"
                                                    >
                                                        <ShieldCheck className="w-4 h-4" />Connect
                                                        {deviceLoadingState[device.id]?.loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                                    </Button>
                                                </div>
                                                : <Button
                                                    size="sm"
                                                    onClick={() => handleConnect(device.id)}
                                                    data-testid={`button-connect-${device.id}`}
                                                    className="tenant-device-primary"
                                                    data-wide="true"
                                                >
                                                    <ShieldCheck className="w-4 h-4" />
                                                    Request Connect Access
                                                </Button>}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {(isLoading || devices.length > 0) && totalPages > 1 && (
                <div className="tenant-endpoints-footer">
                    <CardPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        label="Devices pagination"
                    />
                </div>
            )}

            <RequestConnectAccessDialog
                open={showAccessDialog}
                onOpenChange={setShowAccessDialog}
                onSubmitSuccess={handleRequestSubmitSuccess}
                deviceName={selectedDevice?.name || ""}
                applicationName="Device Access"
                osType={selectedDevice ? getOSType(selectedDevice) : "windows"}
                userList={usersList}
                sessionRequest={true}
                resourceType="GROUP"
                resourcseId={selectedDevice?.whiteswanAccessGroupId}
                isWhitewanAccess={"true"}
                isFromEndPoint={true}
                isFromEndPointApp={false}
                tenantName={tenantName}
                device={selectedDevice}
                publisherId={""}
                resourceName={"Whiteswan Access"}
                resourcePath={"Whiteswan Accesss"}
            />
        </section>
    );
}
